import React from 'react';

import { CheckboxProps as CheckboxPropsM } from '@mui/material/Checkbox';
import { ListItemButtonProps as ListItemButtonPropsM } from '@mui/material/ListItemButton';

import Box from '../Box/Box.jsx';
import Checkbox from '../Checkbox/Checkbox.jsx';
import Icon from '../Icon/Icon.jsx';
import Truncation, { TruncationProps } from '../Truncation/Truncation.jsx';
import { DragIconPositionVariants } from './DraggableItem.constants.js';
import StyledDraggableItem from './DraggableItem.styles.jsx';

export interface DraggableItemProps extends ListItemButtonPropsM {
  text: string;
  onItemClick?: (...args: any) => void;
  onCheckboxAreaClick?: (...args: any) => void;
  iconPosition?: DragIconPositionVariants;
  CheckboxProps?: CheckboxPropsM;
  checkbox?: boolean;
  isDragged?: boolean;
  disabled?: boolean;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  DraggableIconProps?: any;
  LabelProps?: TruncationProps;
  description?: string;
}

const DraggableItem: React.FC<DraggableItemProps> = ({
  text,
  onItemClick,
  CheckboxProps,
  iconPosition = DragIconPositionVariants.Left,
  endAdornment,
  startAdornment,
  checkbox,
  isDragged,
  disabled = false,
  DraggableIconProps,
  onCheckboxAreaClick,
  LabelProps,
  description,
  ...props
}) => {
  const dragIcon = (
    <Icon
      component="Draggable"
      sx={{ color: disabled ? 'text.disabled' : 'icon.secondary' }}
      {...DraggableIconProps}
    />
  );

  return (
    <StyledDraggableItem
      onClick={(e) => onItemClick && onItemClick(e)}
      isDragged={isDragged}
      checkbox={checkbox}
      disabled={disabled}
      description={description}
      {...props}
    >
      {checkbox ? (
        <Box
          onClick={onCheckboxAreaClick}
          sx={{
            padding: '0.375rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0.5rem 0 0 0.5rem',
            ':hover': {
              backgroundColor: !disabled ? 'ui.hover' : 'transparent.main',
            },
          }}
        >
          <Checkbox disabled={disabled} {...CheckboxProps} />
        </Box>
      ) : null}
      {iconPosition === DragIconPositionVariants.Left ? dragIcon : null}
      {startAdornment}
      <Box sx={{ display: 'grid', width: '100%', rowGap: '4px' }}>
        <Truncation tooltipTitle={text} variant="bodyShort02" {...LabelProps}>
          {text}
        </Truncation>
        {description && (
          <Truncation
            tooltipTitle={description}
            variant="caption02"
            color={(theme) => theme.palette.text.tertiary}
          >
            {description}
          </Truncation>
        )}
      </Box>
      {endAdornment}
      {iconPosition === DragIconPositionVariants.Right ? dragIcon : null}
    </StyledDraggableItem>
  );
};

export default DraggableItem;
